/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2021, Bulb Inc.  For more information, please email contact@hellobulb.com.
 *
 *                _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
 *
 */

/**
 * @module bulb-image/bulb-image-rotate
 */
import {
  Plugin, ButtonView, ModelElement, type Locale,
} from 'ckeditor5';

import bulbCropIcon from '../../../assets/media_toolbar_icons/crop_image_icon.svg';
import { getDisplayedImageAsset } from '../bulbimageutils';
import { windowsEventListenerChecklist } from '../../utils';
import BulbImageCropUtils from './bulbimagecroputils';

/**
 * The image crop ui plugin class.
 *
 * @extends Plugin
 */
export default class BulbImageCropUI extends Plugin {

  /**
   * @inheritDoc
   */
  public static get pluginName(): string {
    return 'BulbImageCropUI' as const;
  }

  /**
   * @inheritDoc
   */
  public init(): void {
    const { editor } = this;
    let imageIndex: any = null;
    const { t } = editor;

    /**
     * componentFactory for the bulb-image-crop plugin to handle the initial state of the crop plugin UI.
     * This componentFactory deals with setting up and initializing cropperjs on the element that is currently
     * Selected.
     */
    editor.ui.componentFactory.add('bulbCropImage', (locale: Locale): ButtonView => {
      // const editingDocument = this.editor.editing.view.document;
      // The button will be an instance of ButtonView.
      const buttonView = new ButtonView(locale);

      // Set appropriate button view properties:
      buttonView.set({
        label: t('editor_image_bar_crop'),
        icon: bulbCropIcon,
        tooltip: true,
      });

      // Implement the logic for when a user clicks the crop button:
      buttonView.on('execute', () => {
        const modelElement = editor.model.document.selection.getSelectedElement() as ModelElement;
        const blockId = modelElement.getAttribute('bulb-block-id') as string;
        const contentBlock: any = CKEBulbData.page.unit.contentBlocks[blockId];
        // const viewFigure = editor.editing.mapper.toViewElement(modelElement) as any;
        const ckeData = {
          blockId, editor,
        };
        const uncroppedAsset = BulbImageCropUtils.getUncroppedImageAsset(contentBlock, null);
        CKEBulbData.service.imageDialog.show(uncroppedAsset, false, this.handleSave, 'FREEFORM', 465, 300, ckeData);
      });

      window.addEventListener('dispatchSlideIndex', (event: any) => {
        imageIndex = event.detail.activeIndex;
        if (event.detail.activeIndex === -1) {
          imageIndex = 0;
        }
      });
      if (!windowsEventListenerChecklist.imageCropAdded) {
        // Listen for the event that is going to be fired from our client that the upload was successful.
        window.addEventListener('imageCropComplete', (event: any) => {
          const { contentBlock, currentEditor } = event.detail as any;

          if (contentBlock.blockType === 'IMAGE') {
            const croppedImageAsset = getDisplayedImageAsset(contentBlock);
            croppedImageAsset.version = contentBlock.version;
            currentEditor.execute('cropBulbImage', croppedImageAsset, null);
          }
        });

        // Make it so these events can't be added again since the crop plugin is initialized several times.
        windowsEventListenerChecklist.imageCropAdded = true;
      }

      // this.setButtonViewBinds(buttonView, false, t, editor);

      return buttonView;
    });
  }

  /**
   * Call on image crop completed before save.
   * @param response
   */
  handleSave(response: any) {
    const { croppedData, extra } = response;
    const imagePatch = {
      cropRectangle: {
        x: croppedData.x, y: croppedData.y, width: croppedData.width, height: croppedData.height,
      },
      selectedImageIndex: undefined,
    };
    const { blockId } = extra;
    const { editor } = extra;
    const bulbCropImageEvent = new CustomEvent('bulbCropImage', {
      detail: {
        imagePatch, blockId, editor,
      },
    });
    window.dispatchEvent(bulbCropImageEvent);
  }
}
